"use client";

import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import SectionsGridSkeleton from "@/components/skeletons/SectionsGridSkeleton";
import Section from "./Section";
import SectionHeading from "./SectionHeading";
import CategoryTile from "./CategoryTile";
import { useSections } from "../hooks/useSections";
import { partitionByOther, resolveTopLevelCategories } from "../utils/categoryGrouping";
import { getClientRegion } from "@/services/region";

type SectionsProps = {
  title?: string;
  /**
   * When true, categories flagged `show_in_other` are also omitted from the
   * grid, AND only categories with `show_on_homepage` are fetched at all -
   * both home-page-only behaviors (see the only_on_homepage request below).
   * Both cases already only show top-level categories; children are reached
   * by drilling into their parent, as in the Navbar - unless the parent
   * isn't available in this Region, in which case the child is promoted to
   * top-level instead (see `resolveTopLevelCategories`).
   */
  primaryOnly?: boolean;
};

export default function Sections({ title = "Sections", primaryOnly = false }: SectionsProps) {
  const locale = useLocale();
  const t = useTranslations("web.courses");
  const pathname = usePathname();
  const regionSlug = getClientRegion(pathname) || undefined;
  // primaryOnly is only ever passed by the home page (see its own doc comment
  // above), so it doubles as the "this is the homepage Categories section"
  // signal for the show_on_homepage filter - the /categories page (primaryOnly
  // false) is unaffected and keeps listing every active category.
  const { sections, loading } = useSections(locale, regionSlug, undefined, undefined, primaryOnly);
  const resolvedTitle = title === "Sections" ? t("categories_heading") : title;
  const topLevelSections = resolveTopLevelCategories(sections);
  const visibleSections = primaryOnly ? partitionByOther(topLevelSections).primary : topLevelSections;

  if (loading) {
    return <SectionsGridSkeleton />;
  }

  if (visibleSections.length === 0) {
    return null;
  }

  return (
    <Section tone="tinted">
      <SectionHeading align="center">{resolvedTitle}</SectionHeading>

      {/* A real grid rather than the old centred flex-wrap of ~31% widths,
          which left the final row's items floating in the middle. */}
      <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
        {visibleSections.map((section) => (
          <CategoryTile key={section.id} section={section} locale={locale} />
        ))}
      </div>
    </Section>
  );
}
